/**
 * Workflow definition hooks (v1.1-A1 / Phase B4) — load + save the
 * canvas state of /automate/workflows/:id against the
 * workflow_definitions table.
 *
 * This is the canvas's ONE load path (AUT-F0-T1): the builder starts
 * empty and hydrates its nodes and edges only from GET
 * /workflow-definitions/:id through serverNodesToRF / serverEdgesToRF.
 * Save always PUTs — if no row existed, this creates one (UNIQUE on
 * automation_id at the API layer means the server treats this as
 * upsert via the route's transactional replace).
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Node, Edge } from '@xyflow/react';
import { apiClient } from './api-client';
import { edgeDefaults } from '@/components/workflow/edge-defaults';

// ─── server VM (mirror of workflow-definitions.ts route) ──────────

export interface ServerWorkflowNode {
  id: string;
  nodeKind: string;
  positionX: number;
  positionY: number;
  label: string | null;
  config: Record<string, unknown>;
  displayOrder: number;
}

export interface ServerWorkflowEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  sourceHandle: string | null;
  /** The input the edge ends on (c96). NULL = the node's first input. */
  targetHandle: string | null;
  label: string | null;
  animated: boolean;
}

export interface ServerWorkflowDefinition {
  id: string;
  automationId: string;
  name: string;
  description: string | null;
  purpose: string | null;
  complexity: 'simple' | 'standard' | 'complex';
  supervisorMode: 'minimal' | 'standard' | 'thorough';
  layout: Record<string, unknown>;
  status: 'draft' | 'active' | 'paused' | 'archived';
  nodes: ServerWorkflowNode[];
  edges: ServerWorkflowEdge[];
  successCriteria: Array<{
    id: string;
    description: string;
    criteriaType: string;
    evaluation: string;
    weight: string;
    displayOrder: number;
  }>;
  constraints: Array<{
    id: string;
    description: string;
    displayOrder: number;
  }>;
  createdAt: string;
  updatedAt: string;
}

// ─── adapters ────────────────────────────────────────────────────

export function serverNodesToRF(nodes: ServerWorkflowNode[]): Node[] {
  return nodes.map((n) => ({
    id: n.id,
    type: n.nodeKind,
    position: { x: n.positionX, y: n.positionY },
    data: {
      label: n.label ?? '',
      config: n.config,
      status: 'idle' as const,
    },
  }));
}

export function serverEdgesToRF(edges: ServerWorkflowEdge[]): Edge[] {
  // The look comes from EDGE_DEFAULTS (the same as a drawn edge); the
  // stored `animated` and both handles are the edge's own. A NULL handle
  // stays null — React Flow draws it on the node's first handle.
  return edges.map((e) => ({
    ...edgeDefaults(),
    id: e.id,
    source: e.sourceNodeId,
    target: e.targetNodeId,
    sourceHandle: e.sourceHandle ?? null,
    targetHandle: e.targetHandle ?? null,
    label: e.label ?? undefined,
    animated: e.animated,
  }));
}

export interface SavePayload {
  name: string;
  description?: string | null;
  purpose?: string | null;
  complexity?: 'simple' | 'standard' | 'complex';
  supervisorMode?: 'minimal' | 'standard' | 'thorough';
  layout?: Record<string, unknown>;
  status?: 'draft' | 'active' | 'paused' | 'archived';
  nodes: Array<{
    id?: string;
    nodeKind: string;
    positionX: number;
    positionY: number;
    label?: string;
    config: Record<string, unknown>;
    displayOrder: number;
  }>;
  edges: Array<{
    id?: string;
    sourceNodeId: string;
    targetNodeId: string;
    sourceHandle?: string;
    targetHandle?: string;
    label?: string;
    animated: boolean;
  }>;
  successCriteria?: Array<unknown>;
  constraints?: Array<unknown>;
}

/**
 * #238 — round-2 / round-3 versions of this helper only mapped
 * `name + nodes + edges`. The PUT `/workflow-definitions/:id`
 * endpoint accepts purpose / successCriteria / constraints /
 * supervisorMode but DEFAULTS them to null / [] when absent.
 * Result: every save wiped the workflow's purpose and quality
 * criteria — and Test Run then had no criteria for the supervisor
 * to evaluate against.
 *
 * The backend's Zod schemas expect:
 *   - successCriteria: { description, criteriaType, evaluation,
 *                        weight, displayOrder }
 *   - constraints:     { description, displayOrder }
 *
 * The frontend `SuccessCriterion` shape uses `type` (not
 * `criteriaType`) and frontend constraints are bare strings; this
 * helper translates both.
 */
export interface RfSaveExtras {
  purpose?: string | null;
  supervisorMode?: 'minimal' | 'standard' | 'thorough';
  successCriteria?: Array<{
    id?: string;
    description: string;
    type: 'completeness' | 'accuracy' | 'format' | 'safety' | 'compliance';
    evaluation: 'ai' | 'deterministic' | 'hybrid';
    weight: 'critical' | 'important' | 'nice_to_have';
  }>;
  constraints?: string[];
}

export function rfToSavePayload(
  name: string,
  rfNodes: Node[],
  rfEdges: Edge[],
  extras: RfSaveExtras = {},
): SavePayload {
  return {
    name,
    purpose: extras.purpose ?? null,
    supervisorMode: extras.supervisorMode ?? 'standard',
    successCriteria: (extras.successCriteria ?? []).map((c, i) => ({
      // Backend Zod requires UUID ids when provided, so omit non-UUID
      // local ids; the backend assigns one on insert.
      ...(c.id && isUuid(c.id) ? { id: c.id } : {}),
      description: c.description,
      criteriaType: c.type,
      evaluation: c.evaluation,
      weight: c.weight,
      displayOrder: i,
    })),
    constraints: (extras.constraints ?? []).map((description, i) => ({
      description,
      displayOrder: i,
    })),
    nodes: rfNodes.map((n, i) => ({
      id: isUuid(n.id) ? n.id : undefined,
      nodeKind: (n.type ?? 'agent') as string,
      positionX: Math.round(n.position.x),
      positionY: Math.round(n.position.y),
      label:
        typeof (n.data as { label?: unknown })?.label === 'string'
          ? ((n.data as { label?: string }).label as string)
          : undefined,
      config:
        ((n.data as { config?: Record<string, unknown> })?.config ?? {}) ??
        {},
      displayOrder: i,
    })),
    edges: rfEdges.map((e) => ({
      id: isUuid(e.id) ? e.id : undefined,
      sourceNodeId: e.source,
      targetNodeId: e.target,
      // A null handle is omitted: the server stores NULL ("the first handle").
      sourceHandle:
        typeof e.sourceHandle === 'string' ? e.sourceHandle : undefined,
      targetHandle:
        typeof e.targetHandle === 'string' ? e.targetHandle : undefined,
      label: typeof e.label === 'string' ? e.label : undefined,
      animated: !!e.animated,
    })),
  };
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function isUuid(s: string): boolean {
  return UUID_RE.test(s);
}

// ─── hooks ────────────────────────────────────────────────────────

/** The canvas graph's one load query: a row, null on 404 (no definition
 *  yet), and an ERROR for anything else — never a silent empty graph. */
export function workflowDefinitionQueryOptions(automationId: string | undefined) {
  return queryOptions<ServerWorkflowDefinition | null, Error>({
    queryKey: ['workflow-definition', automationId],
    enabled: !!automationId && isUuid(automationId ?? ''),
    queryFn: async () => {
      try {
        return await apiClient.get<ServerWorkflowDefinition>(
          `/workflow-definitions/${automationId}`,
        );
      } catch (e) {
        // api-client attaches .status onto the thrown Error.
        if ((e as { status?: number })?.status === 404) return null;
        throw e;
      }
    },
    retry: false,
  });
}

export function useWorkflowDefinition(automationId: string | undefined) {
  return useQuery(workflowDefinitionQueryOptions(automationId));
}

// ─── the canvas load state (AUT-F0-T1-R1) ─────────────────────────
//
// The canvas Save is a PUT that REPLACES the stored graph (the route deletes
// and reinserts every node and edge). So the canvas may only be edited or
// saved once it has been hydrated from THIS workflow's definition GET. Until
// then it is 'loading' (no answer yet, or the canvas still holds another
// workflow's graph) or 'error' (the GET failed with anything but a 404) —
// both refuse the save, so a failed load can never be saved over the stored
// graph as an empty canvas. Nothing fails open.

export type CanvasLoad =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready' };

export const CANVAS_NOT_LOADED_MESSAGE =
  'The workflow has not loaded, so saving now would replace the stored workflow with an empty canvas.';

export function canvasLoadState(input: {
  /** The route's workflow id. */
  id: string;
  /** The id the canvas was last hydrated from (null: never). */
  hydratedFor: string | null;
  /** The definition GET's error state. */
  defIsError: boolean;
  defError?: { message?: string } | null;
}): CanvasLoad {
  // A non-UUID id has no stored definition to load or overwrite (the save
  // path for it never sends a PUT).
  if (!isUuid(input.id)) return { status: 'ready' };
  // Hydrated from this id: a later failed refetch does not lock the canvas.
  if (input.hydratedFor === input.id) return { status: 'ready' };
  if (input.defIsError) {
    return {
      status: 'error',
      message: input.defError?.message || 'The workflow could not be loaded.',
    };
  }
  return { status: 'loading' };
}

export interface SaveWorkflowDefinitionInput {
  /** The canvas load state at the moment of saving. */
  load: CanvasLoad;
  payload: SavePayload;
}

/** The one door to the definition PUT: refuses unless the canvas is ready. */
export async function putWorkflowDefinition(
  automationId: string,
  { load, payload }: SaveWorkflowDefinitionInput,
): Promise<ServerWorkflowDefinition> {
  if (load.status !== 'ready') throw new Error(CANVAS_NOT_LOADED_MESSAGE);
  return apiClient.put<ServerWorkflowDefinition>(
    `/workflow-definitions/${automationId}`,
    payload,
  );
}

export function useUpdateWorkflowDefinition(automationId: string) {
  const qc = useQueryClient();
  return useMutation<ServerWorkflowDefinition, Error, SaveWorkflowDefinitionInput>({
    mutationFn: (input) => putWorkflowDefinition(automationId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definition', automationId] });
    },
  });
}
